<template>
  <div class="base-advanced-table">
    <div class="mb-3 d-flex align-items-center">
      <slot name="table(top-left)">
        <div>
          <template v-for="filterItem in filterList">
            <b-badge
              v-for="item in filterItem.items"
              :key="`${filterItem.key}-${item}`"
              variant="light"
              class="text-primary py-1 px-3 mr-2 border border-primary d-inline-flex align-items-center shadow"
            >
              {{ filterItem.label }}: {{ item }}
              <b-button
                @click.stop.prevent="
                  () => handleSearchColumnRemove(filterItem.key, item)
                "
                size="sm"
                variant="link"
                class="text-danger p-0 ml-2 rounded-circle"
              >
                <i class="fa fa-times"></i>
              </b-button>
            </b-badge>
          </template>
        </div>
      </slot>

      <slot name="table(top-right)"></slot>
    </div>

    <b-card class="mb-0" no-body>
      <slot name="table(head)">
        <b-card-header
          ref="cardHeaderRef"
          header-class="border-bottom-0 d-flex align-items-center"
        >
          <slot name="table(head-left)" :selection="selectionList"></slot>

          <b-button
            v-if="actions.includes('delete') && !!selectionList.length"
            @click="handleBulkDelete"
            variant="link"
            class="p-0 bulk-delete-btn"
            >Delete</b-button
          >

          <div class="navbar-search form-inline ml-auto">
            <b-form-group class="mb-0">
              <b-input-group class="input-group-alternative input-group-merge">
                <div class="input-group-prepend">
                  <span class="input-group-text"
                    ><i class="fas fa-search"></i
                  ></span>
                </div>
                <b-form-input
                  @input="handleSearch"
                  size="sm"
                  placeholder="Search"
                  type="text"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </div>

          <slot name="table(head-right)" />
        </b-card-header>
      </slot>

      <b-card-body ref="cardBodyRef">
        <b-table
          v-bind="$attrs"
          @row-clicked="
            (item, index, event) => $emit('click:row', { item, index, event })
          "
          :fields="tableFields"
          :items="tableItems"
          :current-page="currentPage"
          :per-page="tablePerPage"
          :tbody-tr-class="tableRowClass"
          :sticky-header="`${stickyHeaderHeight}px`"
          class="mb-0"
          fixed
          responsive
          show-empty
          ref="tableRef"
        >
          <template #table-colgroup="scope">
            <col
              v-for="field in scope.fields"
              :key="field.key"
              :style="{ width: field.width || '180px' }"
            />
          </template>

          <template
            v-for="tableField in tableFields"
            v-slot:[`head(${tableField.key})`]="data"
          >
            <slot :name="`head(${tableField.key})`" :data="data.item">
              <template v-if="'select' === tableField.key">
                <b-form-checkbox
                  @change="handleSelectAll"
                  :id="`select-${data.index}`"
                  :checked="
                    !!selectionList.length &&
                    selectionList.length === tableItems.length
                  "
                />
              </template>
              <template>
                {{ data.label }}

                <template v-if="data.field.searchable">
                  <b-button
                    @click.stop
                    :id="`popover-${data.column}`"
                    variant="link"
                    class="p-1 mr-0 position-absolute right-0"
                  >
                    <b-icon
                      icon="search"
                      font-scale="0.95"
                      class="ml-auto"
                    ></b-icon>
                  </b-button>
                  <b-popover
                    :target="`popover-${data.column}`"
                    :custom-class="data.field.isDate ? 'p-0' : 'p-2'"
                    triggers="focus"
                  >
                    <b-calendar
                      v-if="data.field.isDate"
                      v-model="cellSearch[data.column]"
                      @input="() => handleSearchColumnAdd(data.column)"
                    ></b-calendar>
                    <b-form-group v-else-if="!!data.field.filters" class="m-0">
                      <template #label>
                        <small class="fw-bold"
                          >Select {{ data.field.label }}</small
                        >
                      </template>

                      <b-form-radio-group
                        v-model="cellSearch[data.column]"
                        :options="data.field.filters"
                        :id="`id_${data.field.key}`"
                        :name="data.field.key"
                        buttons
                        stacked
                        @change="handleSearchColumnAdd(data.column)"
                      ></b-form-radio-group>
                    </b-form-group>
                    <b-form
                      v-else
                      @submit.prevent="() => handleSearchColumnAdd(data.column)"
                    >
                      <b-input
                        v-model="cellSearch[data.column]"
                        :placeholder="`Search by ${data.label}`"
                        size="sm"
                        class="mb-2"
                      />
                      <b-button
                        variant="primary"
                        size="sm"
                        type="submit"
                        class="ml-auto d-table"
                        >Apply</b-button
                      >
                    </b-form>
                  </b-popover>
                </template>
              </template>
            </slot>
          </template>

          <template
            v-for="tableField in tableFields"
            v-slot:[`cell(${tableField.key})`]="data"
          >
            <slot :name="`cell(${tableField.key})`" :data="data">
              <template v-if="'select' === tableField.key">
                <b-form-checkbox
                  v-model="selectionList"
                  :id="`select-${data.index}`"
                  :value="data.item.id || data.index"
                />
              </template>
              <template v-else-if="'actions' === tableField.key">
                <slot name="action(edit)" :data="data">
                  <b-button
                    v-if="actions.includes('edit')"
                    @click="() => handleEdit(data.item)"
                    variant="link"
                    class="p-0 mr-3 text-light"
                  >
                    <b-icon icon="pencil"></b-icon>
                  </b-button>
                </slot>

                <slot name="action(delete)" :data="data">
                  <b-button
                    v-if="actions.includes('delete')"
                    @click="() => handleDelete(data.item)"
                    variant="link"
                    class="p-0 text-light"
                  >
                    <b-icon icon="trash"></b-icon>
                  </b-button>
                </slot>
              </template>
              <span
                v-else
                @click="() => $emit('click:cell', data)"
                :key="tableField.key"
                >{{ data.item[tableField.key] }}</span
              >
            </slot>
          </template>
        </b-table>
      </b-card-body>

      <b-card-footer
        class="d-flex align-items-center justify-content-center justify-content-sm-between flex-wrap"
        ref="cardFooterRef"
      >
        <div class="d-flex align-items-center font-family-secondary text-sm">
          Show
          <b-form-select
            v-model="perPage"
            :options="perPageOptions"
            size="sm"
            class="mx-2"
          ></b-form-select>
          entries
        </div>

        <b-pagination
          v-model="currentPage"
          @change="onPaginate"
          :total-rows="totalRows || totalItems"
          :per-page="perPage"
          ellipsis-class="d-none"
          class="m-0"
          hide-ellipsis
        />
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  reactive,
  ref,
  onBeforeMount,
  onUnmounted,
  onMounted,
  watch,
  defineExpose
} from '@vue/composition-api'
import { debounce } from 'lodash-es'
import uniqueId from 'lodash.uniqueid'
import Fuse from 'fuse.js'
import moment from 'moment'

const TableMinHeight = 500

export default defineComponent({
  name: 'BaseAdvancedTable',
  inheritAttrs: false,
  emits: [
    'click:cell',
    'click:row',
    'delete:cell',
    'delete:bulk',
    'search',
    'paginate',
    'update:selection-list'
  ],
  props: {
    actions: {
      type: Array,
      default: () => ['delete', 'edit']
    },
    confirmBulkDeleteTitle: String,
    confirmBulkDeleteText: String,
    fitTableBodyToWindow: {
      type: Boolean,
      description:
        'Fits the table body to the page while preserving sticky header',
      default: false
    },
    paginationOptions: {
      type: Array,
      default: () => [5, 10, 25, 50]
    },
    pageSize: Number,
    totalRows: Number,
    serverSide: Boolean
  },
  setup(props, { attrs, emit }) {
    const perPageOptions = props.paginationOptions

    const cardHeaderRef = ref()
    const cardBodyRef = ref()
    const cardFooterRef = ref()
    const stickyHeaderHeight = ref(TableMinHeight)

    const searchKeyword = ref('')
    const currentPage = ref(1)
    const perPage = ref(props.pageSize || 10)
    const bulkSelection = ref(false)
    const selectionList = ref([])
    const filterList = ref([])
    const deletingList = ref([])

    const cellSearch = reactive({})

    const tablePerPage = computed(() => {
      if (props.serverSide || 'All' == perPage.value) return 0

      return perPage.value
    })

    const totalItems = computed(() =>
      searchKeyword.value || !!searchColumns.value.length
        ? tableItems.value.length
        : attrs.items.length
    )

    const searchKeys = computed(() =>
      attrs.fields.filter((item) => !!item.searchable).map((item) => item.key)
    )

    const searchColumns = computed(() => {
      let filters = []

      filterList.value
        .filter((item) => !!item.items.length)
        .forEach((item) => {
          const items = item.items.map((keyword) => {
            if (!!item.keys.length) {
              return {
                $or: item.keys.map((key) => ({
                  [key]: `'${keyword}`
                }))
              }
            } else {
              return {
                $path: [item.key],
                $val: `'${keyword}`
              }
            }
          })

          filters = filters.concat(items)
        })

      return filters
    })

    const onPaginate = (page) => {
      bulkSelection.value = false

      emit('paginate', page)
    }

    const onResize = debounce(() => {
      if (!props.fitTableBodyToWindow) {
        return
      }
      const $cardHeader = cardHeaderRef.value
      const $cardFooter = cardFooterRef.value
      const $cardBody = cardBodyRef.value
      if (!($cardHeader && $cardFooter && $cardBody)) {
        return
      }

      //Resize the table head and body container to fit the target container (default the window/screen)
      //Keeps the footer visible if possible
      let fitToContainerHeight = window.innerHeight

      const headerRect = $cardHeader.getBoundingClientRect()
      const footerRect = $cardFooter.getBoundingClientRect()
      const bodyStyles = window.getComputedStyle($cardBody)
      const offsetPadding = Math.abs(
        parseFloat(bodyStyles.paddingTop) + parseFloat(bodyStyles.paddingBottom)
      )
      let finalHeight = Math.abs(
        fitToContainerHeight -
          headerRect.bottom -
          footerRect.height -
          offsetPadding
      )
      finalHeight = Math.max(TableMinHeight, finalHeight)
      stickyHeaderHeight.value = finalHeight
    }, 20)
    onBeforeMount(() => {
      window.addEventListener('resize', onResize, true)
    })
    onMounted(() => {
      onResize()
    })
    onUnmounted(() => {
      window.removeEventListener('resize', onResize)
    })

    const tableFields = computed(() =>
      [
        props.actions.includes('delete')
          ? {
              key: 'select',
              label: '',
              width: '55px',
              searchable: false,
              sortable: false
            }
          : null,
        ...attrs.fields,
        props.actions.includes('edit') || props.actions.includes('delete')
          ? {
              key: 'actions',
              label: '',
              width:
                props.actions.includes('edit') &&
                props.actions.includes('delete')
                  ? '95px'
                  : '55px',
              searchable: false,
              sortable: false,
              class: 'position-sticky right-0'
            }
          : null
      ].filter((item) => item && !item.hide)
    )

    const tableItems = computed(() => {
      let items = attrs.items

      const formattedFields = tableFields.value.filter(({ dateFormat }) => {
        return !!dateFormat
      })

      items = items.map((item) => {
        if (!!formattedFields.length) {
          formattedFields.forEach(({ key, dateFormat }) => {
            if (!!dateFormat) {
              item[key] = !!item[key]
                ? moment(item[key]).format(dateFormat)
                : ''
            }
          })
        }

        return item
      })

      if (!!searchKeys.value.length) {
        const fuse = new Fuse(items, {
          keys: searchKeys.value,
          includeScore: true,
          useExtendedSearch: true
        })

        if (!props.serverSide && searchKeyword.value) {
          items = fuse
            .search(`'${searchKeyword.value}`)
            .map((item) => item.item)
        }

        if (!!searchColumns.value.length) {
          items = fuse
            .search({
              $and: searchColumns.value
            })
            .map((item) => item.item)
        }
      }

      return items.map((item) => ({
        ...item,
        uniqueId: uniqueId(`td-`)
      }))
    })

    const tableRowClass = (item) =>
      item && deletingList.value.includes(item.uniqueId)
        ? 'text-white bg-translucent-danger processing'
        : ''

    const handleEdit = (data) => {
      emit('edit:cell', data)
    }

    const handleDelete = async (data) => {
      const { isConfirmed } = await Vue.prototype.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this.",
        type: 'warning',
        confirmButtonText: 'Yes, delete it!'
      })

      if (!isConfirmed) return

      deletingList.value.push(data.uniqueId)

      emit('delete:cell', data)
    }

    const handleBulkDelete = async () => {
      const items = tableItems.value.filter((item, index) =>
        selectionList.value.includes(item.id || index)
      )

      const { isConfirmed } = await Vue.prototype.$swal({
        title: props.confirmBulkDeleteTitle || 'Delete Items?',
        text:
          props.confirmBulkDeleteText || "You won't be able to revert this.",
        type: 'warning',
        confirmButtonText: 'Delete'
      })

      if (!isConfirmed) return

      deletingList.value = items.map((item) => item.uniqueId)

      emit('delete:bulk', items)
    }

    const handleSearch = debounce((value) => {
      searchKeyword.value = value
      bulkSelection.value = false
      selectionList.value = []

      emit('search', value)
    }, 500)

    const handleSearchColumnAdd = (column) => {
      const field = attrs.fields.find((item) => column === item.key)

      if (!field) return

      const filterItem = filterList.value.find((item) => column === item.key)
      const filterValue = field.isDate
        ? moment(cellSearch[column]).format(field.dateFormat || 'M/DD/YYYY')
        : cellSearch[column]

      if (filterItem) {
        filterItem.items.push(filterValue)
      } else {
        filterList.value.push({
          label: field.label,
          key: field.key,
          keys: field.keys || [],
          items: [filterValue]
        })
      }

      cellSearch[column] = ''
    }

    const handleSearchColumnRemove = (column, keyword) => {
      const filterItem = filterList.value.find((item) => column === item.key)

      if (!filterItem) return

      filterItem.items.splice(filterItem.items.indexOf(keyword), 1)
    }

    const handleSelectAll = (selectAll) => {
      if (selectAll)
        selectionList.value = tableItems.value.map(
          (tableItem, tableItemKey) => {
            return tableItem.id || tableItemKey
          }
        )
      else selectionList.value = []
    }
    const clearSelectionList = () => {
      selectionList.value = []
    }
    watch(selectionList, (newList) => {
      emit('update:selection-list', newList)
    })

    return {
      moment,
      perPageOptions,
      currentPage,
      perPage,
      bulkSelection,
      selectionList,
      filterList,
      cellSearch,
      totalItems,
      tablePerPage,
      searchColumns,
      tableFields,
      tableItems,
      tableRowClass,
      handleEdit,
      handleDelete,
      handleBulkDelete,
      handleSearch,
      handleSearchColumnAdd,
      handleSearchColumnRemove,
      handleSelectAll,
      onPaginate,
      cardHeaderRef,
      cardBodyRef,
      cardFooterRef,
      stickyHeaderHeight,
      clearSelectionList
    }
  }
})
</script>

<style scoped>
>>> .table-responsive {
  height: 100vh;
}

>>> .table > thead > tr > th {
  background-position: right 27px center !important;
  padding: 8px 16px;
  border: 0;
  font-weight: 400;
  vertical-align: middle;
  position: sticky !important;
}
>>> .table > thead > tr > th::before,
>>> .table > thead > tr > th::after {
  content: '';
  width: 100%;
  position: absolute;
  left: 0;
}

>>> .table > thead > tr > th::before {
  border-top: 1px solid #e9ecef;
  top: 0;
}

>>> .table > thead > tr > th::after {
  border-bottom: 2px solid #1c4a50;
  bottom: 0;
}

>>> .table > tbody > tr.processing {
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.15) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.15) 75%,
    transparent 75%,
    transparent
  );
  background-size: 1rem 1rem;
  -webkit-animation: 1s linear infinite progress-bar-stripes;
  animation: 1s linear infinite progress-bar-stripes;
  pointer-events: none;
}

>>> .table > tbody > tr > td {
  padding: 16px;
  white-space: pre-wrap;
}

>>> .table > tbody > tr > td.position-sticky {
  background-color: #fff;
}

>>> .table > tbody > tr > td.position-sticky::before {
  content: '';
  width: 5px;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  box-shadow: rgba(0, 0, 0, 0.1) -4px 0px 8px 0;
}

>>> .btn-link {
  box-shadow: none !important;
}

>>> .badge {
  height: 25px;
  border-radius: 15px;
  font-size: 12px;
}

>>> .custom-control .custom-control-label {
  padding-left: 20px;
  margin-bottom: 0;
}

>>> .custom-select {
  min-width: 60px;
}

>>> .btn-group-toggle {
  width: 100%;
}

>>> .btn-group-toggle .btn {
  width: 100%;
  padding: 10px 15px;
  border-radius: 0;
  font-size: 14px;
  text-align: left;
  box-shadow: none;
}
</style>
